import { jwtDecode } from "jwt-decode";
import strings from "global/constants/StringConstants";
import notifiers from "global/constants/NotificationConstants";

/**
 * What core put in the URL fragment when it redirected the buyer here. A fragment is never sent to a
 * server, which is why core uses it for the one-time claim.
 *   #claim=<id>      a resolved purchase waiting to be linked
 *   #status=linked   the purchase is already linked to an account
 *   #error=<code>    invalid_token | temporarily_unavailable | unknown_product
 */
export interface AwsMarketplaceFragment {
  claim?: string;
  status?: string;
  error?: string;
}

export const parseAwsMarketplaceFragment = (hash: string): AwsMarketplaceFragment => {
  const params = new URLSearchParams(hash.startsWith("#") ? hash.substring(1) : hash);
  return {
    claim: params.get("claim") ?? undefined,
    status: params.get("status") ?? undefined,
    error: params.get("error") ?? undefined,
  };
};

/** The page copy for an error code core sent back. Unknown codes fall back to the token message. */
export const awsMarketplaceErrorMessage = (code: string): string => {
  switch (code) {
    case "temporarily_unavailable":
      return notifiers.AWS_MARKETPLACE_UNAVAILABLE;
    case "unknown_product":
      return notifiers.AWS_MARKETPLACE_UNKNOWN_PRODUCT;
    default:
      return notifiers.AWS_MARKETPLACE_INVALID_TOKEN;
  }
};

interface StoredClaim {
  claim: string;
  savedAt: number;
}

/**
 * Kept in localStorage (not sessionStorage) so it survives signing up: a new buyer verifies their email,
 * possibly in another tab, and comes back. It only lets the signed-in admin link the purchase to their
 * own account, it expires with the server-side claim, and it is removed as soon as it is used or refused.
 * Storage can be unavailable (private mode, blocked site data), so every access is guarded.
 */
export const saveAwsMarketplaceClaim = (claim: string): void => {
  try {
    const value: StoredClaim = { claim, savedAt: Date.now() };
    localStorage.setItem(strings.AWS_MARKETPLACE_CLAIM_KEY, JSON.stringify(value));
  } catch {
    // Without storage the claim only lives for this page view; the buyer can reopen setup from AWS.
  }
};

export const loadAwsMarketplaceClaim = (): string | undefined => {
  try {
    const raw = localStorage.getItem(strings.AWS_MARKETPLACE_CLAIM_KEY);
    if (!raw) return undefined;
    const stored = JSON.parse(raw) as StoredClaim;
    if (!stored?.claim || Date.now() - stored.savedAt > strings.AWS_MARKETPLACE_CLAIM_TTL_MS) {
      clearAwsMarketplaceClaim();
      return undefined;
    }
    return stored.claim;
  } catch {
    return undefined;
  }
};

export const clearAwsMarketplaceClaim = (): void => {
  try {
    localStorage.removeItem(strings.AWS_MARKETPLACE_CLAIM_KEY);
  } catch {
    // nothing to clear
  }
};

/**
 * A persisted `authenticated` flag can outlive the access token, so check the token's own expiry
 * (with 30s of clock skew). jwtDecode, as in utils/service.ts: JWT segments are base64url, which
 * plain atob() rejects whenever the payload encodes to '-' or '_'.
 */
export const isAccessTokenExpired = (jwt?: string | null): boolean => {
  if (!jwt) return true;
  try {
    const { exp } = jwtDecode<{ exp?: number }>(jwt);
    return !exp || exp * 1000 <= Date.now() + 30_000;
  } catch {
    return true;
  }
};
